/**
 * Mobile Tendering — read-mostly enquiry list with stage-update quick action.
 * Reuses existing /api/enquiries endpoints (no new backend).
 * Document upload and full SVR capture stay desktop-only — heavier workflows
 * not suited to a phone screen.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, FileSignature, Search, Loader2, Calendar, X, ChevronRight, AlertCircle } from 'lucide-react';
import { apiGet, apiPut, apiPost } from '../../lib/api';

const inr = (n) => n == null ? '—' : `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
import { formatDate as fmt } from '../../lib/format';

// Tendering_30Sep2026 §5: a 422's `detail` can be an object ({code, message}) —
// passing that straight to toast.error crashes the screen. Always resolve to text.
// Exported so other Tendering screens (e.g. EnquiryRevisionsTab.jsx) reuse the same logic.
export const errText = (e, fallback) => {
  const d = e?.response?.data?.detail ?? e?.response?.data;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object') return d.message || fallback;
  return fallback;
};

const daysUntil = (iso) => {
  if (!iso) return null;
  try {
    const diffMs = new Date(iso).getTime() - new Date().setHours(0, 0, 0, 0);
    return Math.ceil(diffMs / 86400000);
  } catch { return null; }
};

export default function MobileTendering() {
  const [rows, setRows] = useState([]);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const [stageBusy, setStageBusy] = useState(false);
  // Tendering_30Sep2026 §5: Lost/On Hold need the same mandatory reason desktop
  // collects — {stage, reason, reviewDate} while the inline prompt is open.
  const [pendingStage, setPendingStage] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [enq, st] = await Promise.all([
        apiGet('/enquiries'),
        apiGet('/enquiries/config/stages').catch(() => ({ stages: [] })),
      ]);
      // /enquiries returns {items, by_stage, stages, total} — unwrap and
      // always default to []; never fall through to the wrapper object,
      // otherwise rows.filter() crashes the screen.
      const list = Array.isArray(enq) ? enq : (enq?.items || []);
      setRows(Array.isArray(list) ? list : []);
      setStages(st?.stages || []);
    } catch (e) {
      toast.error('Failed to load enquiries');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = (rows || []).filter(r => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return (r.project_name || '').toLowerCase().includes(needle) ||
           (r.client || '').toLowerCase().includes(needle) ||
           (r.enquiry_no || '').toLowerCase().includes(needle);
  });

  const updateStage = async (enq, newStage) => {
    setStageBusy(true);
    try {
      // PUT /enquiries/{id} expects full payload — pass the existing enquiry with stage swapped
      await apiPut(`/enquiries/${enq.id}`, { ...enq, stage: newStage });
      toast.success(`Moved to ${newStage}`);
      setSelected(null);
      load();
    } catch (e) {
      toast.error(errText(e, 'Stage update failed'));
    } finally { setStageBusy(false); }
  };

  // Lost and On Hold have dedicated backend endpoints requiring a reason
  // (mirrors EnquiryDetail.jsx's doMarkLost/doPutOnHold on desktop).
  const stageTap = (enq, newStage) => {
    if (newStage === 'Lost' || newStage === 'On Hold') {
      setPendingStage({ enq, stage: newStage, reason: '', reviewDate: '' });
      return;
    }
    updateStage(enq, newStage);
  };

  const confirmPendingStage = async () => {
    if (!pendingStage) return;
    const reason = pendingStage.reason.trim();
    if (!reason) { toast.error('A reason is required.'); return; }
    setStageBusy(true);
    try {
      if (pendingStage.stage === 'Lost') {
        await apiPost(`/enquiries/${pendingStage.enq.id}/mark-lost`, { reason });
      } else {
        await apiPost(`/enquiries/${pendingStage.enq.id}/put-on-hold`, {
          reason, review_date: pendingStage.reviewDate.trim() || null,
        });
      }
      toast.success(`Moved to ${pendingStage.stage}`);
      setPendingStage(null);
      setSelected(null);
      load();
    } catch (e) {
      toast.error(errText(e, 'Stage update failed'));
    } finally { setStageBusy(false); }
  };


  return (
    <div data-testid="mobile-tendering" className="min-h-screen bg-slate-50 pb-24">
      <header className="bg-[#3b4872] text-white px-4 py-3 sticky top-0 z-10 flex items-center gap-3">
        <Link to="/m" className="p-1 hover:bg-white/10 rounded-sm"><ArrowLeft size={20} /></Link>
        <FileSignature size={20} />
        <div className="flex-1">
          <div className="font-display font-bold text-base">Tendering</div>
          <div className="text-[10px] text-white/70">{filtered.length} enquiries</div>
        </div>
      </header>

      <div className="p-3 sticky top-[52px] bg-slate-50 z-[5]">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="ten-search" value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search title, client, enquiry id…"
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-sm text-sm" />
        </div>
      </div>

      <div className="px-3 space-y-2">
        {loading && <div className="text-center py-8 text-slate-500"><Loader2 className="animate-spin inline" /></div>}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-8 text-slate-500 text-sm">No enquiries match.</div>
        )}
        {!loading && filtered.map(r => {
          const d = daysUntil(r.submission_deadline || r.due_date);
          const urgent = d != null && d <= 7 && d >= 0;
          return (
            <button key={r.id} data-testid={`ten-row-${r.id}`}
              onClick={() => setSelected(r)}
              className={`w-full text-left bg-white border rounded-sm p-3 hover:shadow-sm active:scale-[0.99] transition-all ${urgent ? 'border-amber-400' : 'border-slate-200'}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[10px] text-slate-500">{r.enquiry_no}</div>
                  <div className="font-medium text-sm truncate mt-0.5">{r.project_name || '—'}</div>
                  <div className="text-xs text-slate-500 truncate mt-0.5">{r.client || '—'}</div>
                </div>
                <ChevronRight size={16} className="text-slate-400 flex-shrink-0" />
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 bg-slate-100 text-slate-700 border border-slate-200 rounded-sm">
                  {r.stage || '—'}
                </span>
                {(r.submission_deadline || r.due_date) && (
                  <span className={`text-[10px] font-medium flex items-center gap-1 ${urgent ? 'text-amber-700' : 'text-slate-500'}`}>
                    {urgent && <AlertCircle size={11} />}
                    <Calendar size={11} /> {fmt(r.submission_deadline || r.due_date)}
                    {urgent && ` · ${d}d`}
                  </span>
                )}
              </div>
              {r.estimated_value && (
                <div className="text-xs font-mono font-bold text-[#3b4872] mt-1">{inr(r.estimated_value)}</div>
              )}
            </button>
          );
        })}
      </div>

      {/* Detail bottom-sheet */}
      {selected && (
        <div className="fixed inset-0 z-30 bg-black/40 flex items-end" onClick={() => { setSelected(null); setPendingStage(null); }}>
          <div className="bg-white w-full rounded-t-2xl p-5 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <div className="font-mono text-[10px] text-slate-500">{selected.enquiry_no}</div>
                <div className="font-display font-bold text-lg text-[#3b4872]">{selected.project_name}</div>
                <div className="text-xs text-slate-500 mt-0.5">{selected.client}</div>
              </div>
              <button onClick={() => { setSelected(null); setPendingStage(null); }} className="p-1 -mr-2"><X size={20} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4 text-xs">
              <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Stage</div><div className="font-medium">{selected.stage || '—'}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Value</div><div className="font-medium font-mono">{inr(selected.estimated_value)}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Due</div><div className="font-medium">{fmt(selected.submission_deadline || selected.due_date)}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Owner</div><div className="font-medium truncate">{selected.owner || '—'}</div></div>
            </div>
            {selected.scope_summary && (
              <div className="mb-4">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Scope</div>
                <div className="text-sm text-slate-700">{selected.scope_summary}</div>
              </div>
            )}
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Move to stage</div>
            <div className="grid grid-cols-2 gap-2">
              {stages.map(s => (
                <button key={s} data-testid={`ten-stage-${s}`}
                  onClick={() => stageTap(selected, s)}
                  disabled={stageBusy || s === selected.stage}
                  className={`py-2 px-2 text-xs font-bold uppercase tracking-wider border rounded-sm ${
                    s === selected.stage ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-default' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                  } disabled:opacity-50`}>
                  {s}
                </button>
              ))}
            </div>
            {pendingStage && (
              <div className="mt-4 border border-amber-200 bg-amber-50 rounded-md p-3 space-y-2" data-testid="ten-stage-reason-panel">
                <div className="text-xs font-bold text-amber-800">
                  Reason required to mark {pendingStage.stage}
                </div>
                <textarea
                  data-testid="ten-stage-reason-input"
                  value={pendingStage.reason}
                  onChange={e => setPendingStage(p => ({ ...p, reason: e.target.value }))}
                  placeholder={pendingStage.stage === 'Lost' ? 'e.g. price / technical / scope / client dropped / other' : 'Reason for putting on hold'}
                  className="w-full border border-amber-300 rounded-sm px-2 py-1.5 text-xs bg-white focus:outline-none"
                  rows={2}
                />
                {pendingStage.stage === 'On Hold' && (
                  <input
                    type="date"
                    data-testid="ten-stage-review-date-input"
                    value={pendingStage.reviewDate}
                    onChange={e => setPendingStage(p => ({ ...p, reviewDate: e.target.value }))}
                    className="w-full border border-amber-300 rounded-sm px-2 py-1.5 text-xs bg-white focus:outline-none"
                  />
                )}
                <div className="flex gap-2">
                  <button data-testid="ten-stage-reason-confirm" onClick={confirmPendingStage} disabled={stageBusy || !pendingStage.reason.trim()}
                    className="flex-1 py-2 bg-[#3b4872] text-white text-xs font-bold uppercase tracking-wider rounded-sm disabled:opacity-50">
                    Confirm
                  </button>
                  <button data-testid="ten-stage-reason-cancel" onClick={() => setPendingStage(null)}
                    className="flex-1 py-2 border border-slate-300 text-slate-600 text-xs font-bold uppercase tracking-wider rounded-sm">
                    Cancel
                  </button>
                </div>
              </div>
            )}
            <p className="text-[11px] text-slate-500 leading-snug mt-4">
              Document uploads and Site Visit Reports live on the web — they're heavier
              workflows not suited to a phone. Open <i>Enquiry Pipeline</i> on a laptop
              for the full experience.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
